import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"
// import { buttonVariants } from "@/components/ui/button"

// Simplified local implementation of AlertDialog primitives to avoid external dependency issues
// Mimics basic structure for functionality, NOW WITH PORTALS to ensure z-index priority.

const AlertDialogContext = React.createContext<{ open: boolean; onOpenChange: (open: boolean) => void } | null>(null);

const AlertDialog = ({ children, open, onOpenChange }: { children: React.ReactNode; open?: boolean; onOpenChange?: (open: boolean) => void }) => {
    return (
        <AlertDialogContext.Provider value={{ open: !!open, onOpenChange: onOpenChange || (() => { }) }}>
            {open ? children : null}
        </AlertDialogContext.Provider>
    );
};

const AlertDialogTrigger = ({ children }: { children: React.ReactNode, asChild?: boolean }) => {
    return <>{children}</>;
};

// Portal to body to escape stacking contexts
// Checking for document existence to be safe (SSR friendly-ish, though this is client-only app)
const AlertDialogPortal = ({ children }: { children: React.ReactNode }) => {
    if (typeof document === "undefined") return null;
    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-start justify-center sm:items-center pointer-events-none">
            {/* Inner wrapper to manage layout if needed, using pointer-events-none so overlay handles clicks? 
                Actually, Radix uses Overlay + Content.
                The Portal just places them in Body.
            */}
            {children}
        </div>,
        document.body
    );
};

const AlertDialogOverlay = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            "fixed inset-0 z-[100] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 pointer-events-auto",
            className
        )}
        {...props}
    />
))
AlertDialogOverlay.displayName = "AlertDialogOverlay"

const AlertDialogContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
    return (
        <AlertDialogPortal>
            <AlertDialogOverlay />
            <div
                ref={ref}
                className={cn(
                    "fixed left-[50%] top-[50%] z-[100] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg pointer-events-auto",
                    className
                )}
                {...props}
                onClick={(e) => e.stopPropagation()}
            />
        </AlertDialogPortal>
    );
})
AlertDialogContent.displayName = "AlertDialogContent"

const AlertDialogHeader = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col space-y-2 text-center sm:text-left",
            className
        )}
        {...props}
    />
)
AlertDialogHeader.displayName = "AlertDialogHeader"

const AlertDialogFooter = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
    <div
        className={cn(
            "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
            className
        )}
        {...props}
    />
)
AlertDialogFooter.displayName = "AlertDialogFooter"

const AlertDialogTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => (
    <h2
        ref={ref}
        className={cn("text-lg font-semibold", className)}
        {...props}
    />
))
AlertDialogTitle.displayName = "AlertDialogTitle"

const AlertDialogDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => (
    <p
        ref={ref}
        className={cn("text-sm text-muted-foreground", className)}
        {...props}
    />
))
AlertDialogDescription.displayName = "AlertDialogDescription"

import { buttonVariants } from "@/components/ui/button"

const AlertDialogAction = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(({ className, ...props }, ref) => (
    <button
        ref={ref}
        className={cn(buttonVariants(), className)}
        {...props}
    />
))
AlertDialogAction.displayName = "AlertDialogAction"

const AlertDialogCancel = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(({ className, onClick, ...props }, ref) => {
    const context = React.useContext(AlertDialogContext);
    return (
        <button
            ref={ref}
            type="button"
            className={cn(
                buttonVariants({ variant: "outline" }),
                "mt-2 sm:mt-0",
                className
            )}
            onClick={(e) => {
                onClick?.(e);
                context?.onOpenChange(false);
            }}
            {...props}
        />
    )
})
AlertDialogCancel.displayName = "AlertDialogCancel"

export {
    AlertDialog,
    AlertDialogPortal,
    AlertDialogOverlay,
    AlertDialogTrigger,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogAction,
    AlertDialogCancel,
}
